import { Controller } from "@hotwired/stimulus"
import Quill from "quill/core"
import Snow from "quill/themes/snow"
import Toolbar from "quill/modules/toolbar"
import Bold from "quill/formats/bold"
import Italic from "quill/formats/italic"
import Underline from "quill/formats/underline"
import Strike from "quill/formats/strike"
import Script from "quill/formats/script"
import { SizeStyle } from "quill/formats/size"
import { ColorStyle } from "quill/formats/color"
import { BackgroundStyle } from "quill/formats/background"
import List from "quill/formats/list"
import Indent from "quill/formats/indent"
import Link from "quill/formats/link"
import Blockquote from "quill/formats/blockquote"
import Header from "quill/formats/header"
import Image from "quill/formats/image"
import { AlignStyle } from "quill/formats/align"
import { absolutizeLinkUrl } from "../../utils/link_url"

// Dangerous schemes are left untouched so Quill's own whitelist still rejects them.
class AbsoluteLink extends Link {
  static sanitize(url: string) {
    return super.sanitize(absolutizeLinkUrl(url))
  }
}

Quill.register({
  "modules/toolbar": Toolbar,
  "themes/snow": Snow,
  "formats/bold": Bold,
  "formats/italic": Italic,
  "formats/underline": Underline,
  "formats/strike": Strike,
  "formats/script": Script,
  "formats/list": List,
  "formats/indent": Indent,
  "formats/link": AbsoluteLink,
  "formats/blockquote": Blockquote,
  "formats/header": Header,
  "formats/image": Image,
});

SizeStyle.whitelist = [
  "6pt", "8pt", "9pt", "10pt", "11pt",
  "12pt", "14pt", "16pt", "24pt",
  "26pt", "28pt", "36pt", "48pt", "72pt",
]
Quill.register(SizeStyle, true)
Quill.register(ColorStyle, true)
Quill.register(BackgroundStyle, true)
Quill.register(AlignStyle, true)

export default class EmailEditorController extends Controller {
  declare bodyTarget: HTMLInputElement
  declare editorTarget: HTMLTextAreaElement
  declare formTarget: HTMLFormElement
  declare toolbarTarget: HTMLDivElement
  declare quill: Quill
  declare logoUrlValue: string

  static targets = [
    "body",
    "editor",
    "form",
    "toolbar",
  ]

  static values = {
    logoUrl: String,
  }

  editorTargetConnected() {
    this.quill = new Quill(this.editorTarget, {
      placeholder: 'Type your text here...',
      modules: {
        toolbar: this.toolbarTarget,
        // Dropped files would be inlined as base64, which mail clients refuse to show
        uploader: { mimetypes: [] },
      },
      bounds: this.formTarget,
      theme: 'snow'
    });

    const emailBody = this.bodyTarget.value
    if (emailBody.length > 0) {
      const delta = this.quill.clipboard.convert({ html: emailBody })
      this.quill.setContents(delta, "silent")
    }
  }

  formTargetConnected(el: HTMLFormElement) {
    el.addEventListener("formdata", (event) => {
      event.formData.set(this.bodyTarget.name, this.quill.getSemanticHTML())
    })
  }

  insertToken(event: Event) {
    event.preventDefault()
    const selection = this.quill.getSelection(true)
    this.quill.insertText(selection.index, "{{firstname}}")
  }

  insertLogo(event: Event) {
    event.preventDefault()
    const selection = this.quill.getSelection(true)
    this.quill.insertEmbed(selection.index, "image", this.logoUrlValue)
    this.quill.setSelection(selection.index + 1, 0)
  }
}
